/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * This script adds functionality to the previous and next buttons for a horizontal scroller.
 * It allows users to navigate through a list of items using buttons, with smooth scrolling.
 */

(function() { // Wrap the code in an IIFE to avoid polluting the global scope
  /**
    * Select the container of the prev and next buttons
    * @type {HTMLElement | null}
    */
  const prevNextButtons = document.querySelector('prev-next-buttons.product-list__prev-next');

  // Check if the container exists in the DOM
  if (prevNextButtons) {
    /**
      * Select the scroller element.
      * @type {HTMLElement | null}
      */
    const scroller = prevNextButtons.parentElement.querySelector('.scroller');

    /**
      * Select the previous button element.
      * @type {HTMLButtonElement | null}
      */
    const prevButton = prevNextButtons.querySelector('.prev-next-button--prev');

    /**
      * Select the next button element.
      * @type {HTMLButtonElement | null}
      */
    const nextButton = prevNextButtons.querySelector('.prev-next-button--next');

    // Check if all required elements exist in the DOM
    if (scroller && prevButton && nextButton) {
      /**
        * Calculate the scroll amount based on the scroller's width.
        * @type {number}
        */
      const scrollAmount = scroller.offsetWidth;

      // Add a click event listener to the prevNextButtons container.
      // This handles clicks on both the previous and next buttons using event delegation.
      prevNextButtons.addEventListener('click', (event) => {
        // Check if the clicked element or any of its parents match the previous button
        if (event.target.closest('.prev-next-button--prev')) {
          // Scroll to the left
          scroller.scrollBy({
            left: -scrollAmount,
            behavior: 'smooth' // Smooth scrolling animation
          });
          // Check if the clicked element or any of its parents match the next button
        } else if (event.target.closest('.prev-next-button--next')) {
          // Scroll to the right
          scroller.scrollBy({
            left: scrollAmount,
            behavior: 'smooth' // Smooth scrolling animation
          });
        }
      });

      /**
        * Function to update the state of the previous and next buttons.
        * Disables the previous button when at the start of the scroll,
        * and the next button when at the end of the scroll.
        */
      const updateButtonState = () => {
        // Disable the previous button if the scroller is at the start (leftmost position)
        prevButton.disabled = scroller.scrollLeft === 0;
        // Disable the next button if the scroller is at the end (rightmost position)
        nextButton.disabled = scroller.scrollLeft + scroller.offsetWidth >= scroller.scrollWidth;
      };

      // Attach the updateButtonState function to the scroller's scroll event.
      scroller.addEventListener('scroll', updateButtonState);

      // Call updateButtonState once initially to set the correct state of the buttons on page load.
      updateButtonState();
    }
  }
})(); // End of the IIFE

(function () {

    //Show BOPIS
  async function showBopis() {

      
    // Runs when recharge widget is loaded
    onRechargeLoaded(function () {
      const quickDrawerOpen = document.querySelector("quick-buy-drawer[open]") || document.querySelector("quick-buy-popover[open]");
      // Initial Load
      if (window.location.href.includes("products") && !quickDrawerOpen) {
        loadCompareAtPrice();
        loadDisclaimer();
      }

      if (window.location.href.includes("products") && !quickDrawerOpen) {
        // Update when selling plan is updated
        document.querySelectorAll(".rc-radio__input").forEach(function (radio) {
          radio.addEventListener("change", function (event) {
            loadCompareAtPrice();
            loadDisclaimer();
          });
        });
      }
    });

      function loadCompareAtPrice() {
          const productPrice = document.querySelector(
              ".rc-radio-group__options label:last-child .price-label"
          );
          const subscriptionPrice = document.querySelector(
              ".rc-radio-group__options label:first-child .price-label"
          );
          const compareAtPriceAdjustment = document.querySelector(
              ".rc-radio-group__options label:first-child .discount-label")?.innerHTML?.split("%")[1];
          if (! subscriptionPrice?.querySelector('.compare-at-price')) {
              const compareAtSpan = document.createElement("span");
              compareAtSpan.innerHTML = "$" + (Math.round(productPrice.innerText.split("$")[1] * (1 - compareAtPriceAdjustment / 100) * 100) / 100).toFixed(2);
              compareAtSpan.classList.add("compare-at-price");
              subscriptionPrice.append(compareAtSpan);
          }
      };

      function loadDisclaimer() {
        const isSubscription = document
          .querySelector(".subscription-radio")
          .classList.contains("rc-radio--active");

        const disclaimerArray = theme.settings.subscription.bullets;
        console.log(disclaimerArray);

        if (isSubscription) {
          const sellingPlanContainer = document.querySelector(".rc-selling-plans");
          const disclaimer = document.createElement("ul");
          disclaimer.classList.add("recharge-disclaimers");
          disclaimerArray.forEach(function (item) {
            const listItem = document.createElement("li");
            listItem.innerText = item;
            disclaimer.append(listItem);
          });

          sellingPlanContainer.appendChild(disclaimer);
        }
      };

      function onRechargeLoaded(callback) {
          let element = document.querySelector(".rc-widget");
          if (element) {
            if (typeof callback === "function") {
              callback();
            }
          } else {
            setTimeout(() => {
              onRechargeLoaded(callback);
            }, 1000);
          }
        }

        document.addEventListener("openable-element:open", function (event) {
          let quickAddWidget = document.querySelector(
            "[open] [data-recharge-quickadd-widget]"
          );

            if (window.ReChargeWidget) {
              window.ReChargeWidget.destroyAllWidgets();
              let rechargeProductId = quickAddWidget.getAttribute("data-product-id");

              window.ReChargeWidget.createWidget({
                productId: rechargeProductId,
                injectionParent: `#quickadd-recharge-widget-${rechargeProductId}`,
                selectors: {
                  price: ['.price-hidden-rcg']
                }
              });

              setTimeout(() => {
                setPriceAttribute();
                document.querySelector('quick-buy-drawer[open]')?.addEventListener('variant:changed', function(event) {
                  setPriceAttribute(event.detail.variant.price);
                  loadCompareAtPriceInQuickAdd();
                });

                const singleItemButton = document.querySelector(
                  `[open] .rc_widget__option__input--onetime`
                );

                document
                  .querySelector("[open] .rc_widget__option__input--onetime")
                  ?.addEventListener("click", function (event) {
                    resetSellingPlan(event.target.value);
                  });

                document
                  .querySelector("[open] .rc_widget__option__input--subsave")
                  ?.addEventListener("click", function (event) {
                    const planId = document.querySelector(
                      "[open] .rc-selling-plans__dropdown"
                    ).value;
                    checkForSellingPlan(planId);
                  });

                document
                  .querySelector("[open] .rc-selling-plans__dropdown")
                  ?.addEventListener("change", function (event) {
                    checkForSellingPlan(event.target.value);
                  });

                document
                  .querySelectorAll("[open] .rc-radio__input")
                  ?.forEach(function (radio) {
                    radio.addEventListener("change", function (event) {
                      loadCompareAtPriceInQuickAdd();
                      loadDisclaimerInQuickAdd();
                      updateAddToCartButtonTextInQuickAdd();
                    });
                  });

                checkForSellingPlan();
                moveDeliveryOptions();
                styleQuickAddDiscountLabel();
                loadCompareAtPriceInQuickAdd();
                loadDisclaimerInQuickAdd();
                updateAddToCartButtonTextInQuickAdd();
              }, 1000);
            }
          }
        );

        function setPriceAttribute(price) {
          const priceEl = document.querySelector('quick-buy-drawer[open] .price-list') || document.querySelector('quick-buy-popover[open] .price-list');
          if (priceEl) {
            const formattedPrice = price ? price : priceEl.innerText.replace("$", "").replace(".", "").replace("Sale price", "")
            priceEl.dataset.ogPrice = formattedPrice;
          }
        }

        function resetSellingPlan() {
          document.querySelector(
            '[open] .shopify-product-form input[name="selling_plan"]'
          ).value = "";
        }

        function checkForSellingPlan(id) {
          const sellingPlanId =
            id ?? document.querySelector("[open] .rc-selling-plans__dropdown").value;
          const sellingPlanInput = document.querySelector(
            "[open] input[name='selling_plan']"
          );
          if (!sellingPlanInput) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "selling_plan";
            input.value = sellingPlanId;
            document.querySelector("[open] .shopify-product-form").appendChild(input);
          } else {
            sellingPlanInput.value = sellingPlanId;
          }
        }

        function loadDisclaimerInQuickAdd() {
          const isSubscription = document
            .querySelector("[open] .rc_widget__option--subsave")
            .classList.contains("rc-option--active");

          const disclaimerArray = theme.settings.subscription.bullets;

          if (isSubscription) {
            const sellingPlanContainer = document.querySelector(
              "[open] .rc-selling-plans"
            );
            const existingDisclaimer = document.querySelector(
              "[open] .recharge-disclaimers"
            );
            if (!existingDisclaimer) {
              const disclaimer = document.createElement("ul");
              disclaimer.classList.add("recharge-disclaimers");
              disclaimerArray.forEach(function (item) {
                const listItem = document.createElement("li");
                listItem.innerText = item;
                disclaimer.append(listItem);
              });

              sellingPlanContainer.appendChild(disclaimer);
            }
          }
        }

        function moveDeliveryOptions() {
          const container = document.querySelector("[open] [data-plans-container]");
          const target = document.querySelector("[open] [data-option-subsave]");
          target.appendChild(container);
        }

        function loadCompareAtPriceInQuickAdd() {
          const productPriceDiscount = parseInt(
            document.querySelector("[open] [data-discount-value]").dataset.discountValue
          );
          const originalPrice = document.querySelector("[open] .price-list[data-og-price]");
          const originalPriceAmount = originalPrice?.dataset?.ogPrice;

          const widgetSubscriptionPriceContainer = document.querySelector(
            "[open] [data-price-subsave]"
          );

          const widgetOneTimePriceContainer = document.querySelector(
            "[open] [data-price-onetime]"
          );

          const discountAmount = (originalPriceAmount * productPriceDiscount) / 100;
          const finalPrice = formatMoney(originalPriceAmount - discountAmount);
          const comparePrices = `
          <span class="original-price">${formatMoney(originalPriceAmount)}</span>
          <span class="compare-at-price">${finalPrice}</span>
          `;

          widgetSubscriptionPriceContainer.innerHTML = "";
          widgetSubscriptionPriceContainer.insertAdjacentHTML(
            "beforeend",
            comparePrices
          );
          widgetOneTimePriceContainer.innerText = formatMoney(originalPriceAmount);
        }

        function styleQuickAddDiscountLabel() {
          const discountLabel = document.querySelector(
            "[open] [data-label-text-subsave]"
          );
          const mobileContainer = document.querySelector("[data-option-subsave]");
          const discountPercent = document.querySelector('[data-label-discount]').innerText;

          if (discountLabel) {
            const labelSuffix = discountLabel.innerText.trim();
            const wrapper = document.createElement("span");
            const discountContainer = document.createElement("div");
            const discountWrapper = document.createElement("span");

            discountWrapper.classList.add("discount-label", "text--small");
            discountWrapper.dataset.discountValue = discountPercent;
            discountWrapper.innerHTML = `${labelSuffix} ${discountPercent}`;

            discountContainer.appendChild(discountWrapper);
            wrapper.appendChild(discountContainer);

            discountLabel.innerHTML = "";
            discountLabel.appendChild(wrapper);
          }
        }

        function updateAddToCartButtonTextInQuickAdd() {
          const addToCartButton = document.querySelector("[open] .loader-button__text");
          const isSubscription = document
            .querySelector("[open] .rc_widget__option--subsave")
            .classList.contains("rc-option--active");
          let buttonText = isSubscription ? "Add auto-ship" : "Add to Cart";

          if (addToCartButton.innerText.toLowerCase() !== "sold out") {
            addToCartButton.innerText = buttonText;
          }
        }

  } // FIX 2026-07-10: close showBopis. The missing brace was a SyntaxError that disabled this entire file.

        function groupIntoFours(els) {
          let result = [];
          for (let i = 0; i < els.length; i += 4) {
              result.push(els.slice(i, i + 4));
          }
          return result;
        }

        async function updateRemoveUrls() {
          const isCartPage = window.location.pathname === '/cart';
          if (isCartPage) {
            const cartDataRaw = await fetch('/cart.js');
            const cartDataJSON = await cartDataRaw.json();
            const removeLinkEls = document.querySelectorAll('.cart .line-item__remove-button');
            if (removeLinkEls.length > 0 && cartDataJSON?.items?.length > 0) {
              const linksByLineItem = groupIntoFours([...removeLinkEls]);
              cartDataJSON.items.forEach((lineItem, i) => {
                if (linksByLineItem[i]) {
                  linksByLineItem[i]?.forEach((link) => {
                    const correctedUrl = `${window.location.origin}/cart/change?id=${lineItem?.key}&quantity=0`;
                    link.setAttribute('href', correctedUrl)
                  })
                }
              });
            }
          }
        }

        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(() => {
            updateRemoveUrls()
            showBopis()
          }, 500);})
  })();